import { useRef } from 'react';
import {
  BoldIcon,
  CodeIcon,
  ItalicIcon,
  LinkIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { strings } from '@/i18n/strings';

/**
 * Telegram HTML uchun matn muharriri.
 *
 * Nega WYSIWYG emas, teglari ko'rinadigan maydon: yuboriladigan narsa — HTML QATORI, va uni
 * `contenteditable` orqali yasash har doim ortiqcha razmetka qo'shadi (`<div>`, `<span
 * style=...>`), Telegram esa notanish tegni ko'rsa BUTUN xabarni rad etadi. Bu yerda esa
 * odam nimani ko'rsa, aynan o'shani yuboradi — ustiga tugmalar va tayyor `{...}` lar bilan,
 * ya'ni teglarni qo'lda yozish ham shart emas.
 *
 * Yonida oldindan ko'rish turadi va uni SERVER render qiladi — bu komponent razmetkani o'zi
 * talqin qilmaydi.
 */

interface Wrap {
  icon: typeof BoldIcon;
  title: string;
  before: string;
  after: string;
}

const WRAPS: Wrap[] = [
  { icon: BoldIcon, title: strings.notificationTemplates.bold, before: '<b>', after: '</b>' },
  { icon: ItalicIcon, title: strings.notificationTemplates.italic, before: '<i>', after: '</i>' },
  {
    icon: UnderlineIcon,
    title: strings.notificationTemplates.underline,
    before: '<u>',
    after: '</u>',
  },
  {
    icon: StrikethroughIcon,
    title: strings.notificationTemplates.strike,
    before: '<s>',
    after: '</s>',
  },
  { icon: CodeIcon, title: strings.notificationTemplates.code, before: '<code>', after: '</code>' },
  {
    icon: LinkIcon,
    title: strings.notificationTemplates.link,
    before: '<a href="https://">',
    after: '</a>',
  },
];

export interface TelegramHtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Bosilganda kursor joyiga qo'yiladigan `{...}` lar. */
  placeholders: string[];
  id?: string;
  rows?: number;
}

export function TelegramHtmlEditor({
  value,
  onChange,
  placeholders,
  id,
  rows = 8,
}: TelegramHtmlEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  /**
   * Belgilangan matnni teg ichiga oladi; belgilanmagan bo'lsa bo'sh juftlik qo'yib, kursorni
   * ORASIGA qaytaradi. Kursorni tiklash ataylab: usiz har bosishdan keyin odam sichqonchani
   * qayta olib borishi kerak bo'lardi va tugmalar yozishdan sekinroq bo'lib qolardi.
   */
  function surround(before: string, after: string) {
    const field = ref.current;
    if (!field) return;

    const { selectionStart: start, selectionEnd: end } = field;
    const selected = value.slice(start, end);
    onChange(`${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`);

    queueMicrotask(() => {
      field.focus();
      field.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  }

  function insert(text: string) {
    const field = ref.current;
    if (!field) return;

    const { selectionStart: start, selectionEnd: end } = field;
    onChange(`${value.slice(0, start)}${text}${value.slice(end)}`);
    queueMicrotask(() => {
      field.focus();
      field.setSelectionRange(start + text.length, start + text.length);
    });
  }

  return (
    <div className="space-y-2">
      <div
        className="flex flex-wrap gap-1"
        role="toolbar"
        aria-label={strings.notificationTemplates.toolbar}
      >
        {WRAPS.map((wrap) => (
          <Button
            key={wrap.before}
            type="button"
            variant="outline"
            size="sm"
            title={wrap.title}
            aria-label={wrap.title}
            onClick={() => surround(wrap.before, wrap.after)}
          >
            <wrap.icon className="size-4" aria-hidden />
          </Button>
        ))}
      </div>

      <textarea
        id={id}
        ref={ref}
        rows={rows}
        className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
      />

      {placeholders.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground">
            {strings.notificationTemplates.placeholders}
          </span>
          {placeholders.map((name) => (
            <Button
              key={name}
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 font-mono text-xs"
              onClick={() => insert(`{${name}}`)}
            >
              {`{${name}}`}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
